import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Component, computed, effect, inject, signal } from '@angular/core';

import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ModalService } from '../../../../core/services/modal.service';
import { Load } from '../../../../core/models/load.model';
import { LoadSegment } from '../../../../core/models/load-segment.model';
import { ButtonComponent } from '../../../../shared/ui/button/button.component';
import { FormCardComponent } from '../../../../shared/ui/forms/form-card/form-card.component';
import { FormFieldComponent } from '../../../../shared/ui/forms/form-field/form-field.component';
import { FleetService } from '../../../../core/services/fleet.service';
import { SelectComponent } from '../../../../shared/ui/forms/select/select.component';
import { CheckboxComponent } from '../../../../shared/ui/forms/checkbox/checkbox.component';
import { ToastService } from '../../../../shared/ui/toast/toast.service';
import { LoadingService } from '../../../../shared/ui/loading/loading.service';
import { UserService } from '../../../../core/services/user.service';
import { AssetService } from '../../../../core/services/asset.service';
import { LoadService } from '../../../../core/services/load.service';
import { SegmentService } from '../../../../core/services/segment.service';
import { DriverCredentialWarningComponent } from '../../../../shared/ui/driver-credential-warning/driver-credential-warning.component';
import { getLoadEndDate } from '../../../../core/utils/driver-credentials';

@Component({
  selector: 'app-edit-drivers-form',
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    ButtonComponent,
    FormCardComponent,
    FormFieldComponent,
    SelectComponent,
    CheckboxComponent,
    DriverCredentialWarningComponent,
],
  templateUrl: './edit-drivers-form.component.html',
})
export class EditDriversFormComponent {
  private readonly modalService = inject(ModalService);
  private readonly loadService = inject(LoadService);
  private readonly segmentService = inject(SegmentService);
  private readonly fleetService = inject(FleetService);
  private readonly toastService = inject(ToastService);
  private readonly loadingService = inject(LoadingService);
  private readonly fb = inject(FormBuilder);
  private readonly userService = inject(UserService);
  private readonly assetService = inject(AssetService);

  protected readonly load: Load = this.modalService.state()!.data.load;
  protected readonly existingSegments: LoadSegment[] | undefined =
    this.modalService.state()!.data.segments;
  protected readonly onSuccess = this.modalService.state()!.data.onSuccess;
  protected readonly isSaving = signal(false);
  protected form: FormGroup;
  protected isTeamLoad = signal<boolean>(false);
  protected drivers = this.fleetService.companyDriversSignal;

  // Credential warnings (warn-only for MVP) for the currently selected drivers
  protected readonly selectedDriver1Id = signal<string | null>(null);
  protected readonly selectedDriver2Id = signal<string | null>(null);
  protected readonly loadEndDate = getLoadEndDate(this.load.stops);
  protected readonly selectedDriver1 = computed(() =>
    this.userService.activeDrivers().find((d) => d.id === this.selectedDriver1Id()) || null
  );
  protected readonly selectedDriver2 = computed(() =>
    this.userService.activeDrivers().find((d) => d.id === this.selectedDriver2Id()) || null
  );

  // Sorted driver options
  protected driverOptions = computed(() => {
    const drivers = this.userService.activeDrivers();
    return drivers
      .map((driver) => ({
        label: this.userService.getDriverDropdownLabel(driver),
        value: driver.id,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  });

  protected trucks = this.fleetService.companyTrucksSignal;

  // Sorted truck options
  protected truckOptions = computed(() => {
    const trucks = this.assetService.availableTrucks();
    return trucks
      .map((truck) => ({
        label: truck.unitNumber || truck.licensePlate || truck.id,
        value: truck.id,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  });

  protected trailers = this.fleetService.companyTrailersSignal;

  // Sorted trailer options
  protected trailerOptions = computed(() => {
    const trailers = this.assetService.availableTrailers();
    return trailers
      .map((trailer) => {
        let label = trailer.unitNumber || trailer.licensePlate || trailer.id;
        if (trailer.specs?.trailerType) {
          label += ` - ${trailer.specs.trailerType}`;
          if (trailer.specs.trailerLengthFt) {
            label += ` (${trailer.specs.trailerLengthFt} ft)`;
          }
        }
        return {
          label,
          value: trailer.id,
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label));
  });

  constructor() {
    this.form = this.fb.group({
      driver1: [null],
      driver2: [null],
      truck: [null],
      trailer: [null],
    });

    // Initialize form with data from the load
    this.initializeForm();

    this.selectedDriver1Id.set(this.form.get('driver1')?.value || null);
    this.selectedDriver2Id.set(this.form.get('driver2')?.value || null);
    this.form.get('driver1')?.valueChanges.pipe(takeUntilDestroyed()).subscribe((v) => this.selectedDriver1Id.set(v || null));
    this.form.get('driver2')?.valueChanges.pipe(takeUntilDestroyed()).subscribe((v) => this.selectedDriver2Id.set(v || null));

    // Effect to manage enabling/disabling form groups
    effect(() => this.updateFormState());
  }

  private initializeForm(): void {
    this.isTeamLoad.set(this.load.team || false);

    this.form.patchValue(
      {
        driver1: this.load.driver1Id || null,
        driver2: this.isTeamLoad() ? this.load.driver2Id || null : null,
        truck: this.load.truckId || null,
        trailer: this.load.trailerId || null,
        // ... patch other values from this.load
      },
      { emitEvent: false } // Prevent infinite loops
    );
  }

  private updateFormState(): void {
    const isTeamLoad = this.isTeamLoad();

    const driver2Control = this.form.get('driver2');
    if (isTeamLoad) {
      driver2Control?.enable({ emitEvent: false });
    } else {
      driver2Control?.setValue(null, { emitEvent: false });
      this.selectedDriver2Id.set(null);
      driver2Control?.disable({ emitEvent: false });
    }
  }

  protected toggleTeamLoad(checked: boolean): void {
    this.isTeamLoad.set(checked);
  }

  cancel(): void {
    this.modalService.close();
  }

  async save(): Promise<void> {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.isSaving.set(true);

    this.loadingService.show({
      variant: 'overlay',
      title: 'Updating Drivers & Equipment',
      subtitle: 'Saving your changes...',
    });

    const formValue = this.form.getRawValue();
    const driver1Id = formValue.driver1 || null;
    const driver2Id = this.isTeamLoad() ? formValue.driver2 || null : null;
    const truckId = formValue.truck || null;
    const trailerId = formValue.trailer || null;

    if (driver1Id && driver2Id && driver1Id === driver2Id) {
      this.loadingService.hide();
      this.isSaving.set(false);
      this.toastService.show('warning', 'Driver 1 and Driver 2 cannot be the same driver.');
      return;
    }

    const statusOverride =
      this.load.status === 'VERIFIED' && !!(driver1Id || driver2Id)
        ? ('DISPATCHED' as const)
        : undefined;

    try {
      await this.segmentService.assignOrUpdateLoadDrivers(
        this.load,
        {
          driver1Id,
          driver2Id,
          truckId,
          trailerId,
          statusOverride,
        },
        this.existingSegments
      );

      // Soft-delete views for completely unassigned drivers
      try {
        if (!driver1Id && this.load.driver1Id && this.load.driver1Id !== driver2Id) {
          await this.loadService.removeDriverView(
            this.load.id,
            this.load.driver1Id,
            'Driver unassigned via edit form'
          );
        }
        if (!driver2Id && this.load.driver2Id && this.load.driver2Id !== driver1Id) {
          await this.loadService.removeDriverView(
            this.load.id,
            this.load.driver2Id,
            'Co-driver unassigned via edit form'
          );
        }
      } catch (viewErr) {
        console.warn('Failed to soft-delete driver views:', viewErr);
      }

      this.loadingService.hide();
      this.modalService.close();
      this.onSuccess?.();
      setTimeout(() => {
        this.toastService.show(
          'success',
          statusOverride === 'DISPATCHED'
            ? 'Drivers & equipment assigned — Load Dispatched'
            : 'Drivers and Equipment updated successfully',
          {
            title: 'Success',
            duration: 4000,
          }
        );
      }, 100);
    } catch (error) {
      console.error('Error updating load:', error);
      this.loadingService.hide();
      this.toastService.show(
        'error',
        'Failed to update drivers and equipment. Please try again.',
        {
          title: 'Update Failed',
          showClose: true,
          duration: 0,
        }
      );
    } finally {
      this.isSaving.set(false);
    }
  }
}
